import React, { Component } from "react";
import axios from "axios";
import moment from "moment";
import Helmet from "react-helmet";
import Layout from "../../../Layout/layout";
import { apiHost } from "../../../api/apiClient";
import visionTrakLogo from "../../../Assets/visiontrak-logo-colored.png";
import { cacheRunSheetCapability, getRunSheetCapability } from "./runSheetCapability";
import "../../../styles/reports/runSheets.scss";

class RunSheets extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(props.location.search);
    this.state = {
      selectedDate: query.get("runDate") || moment().format("YYYY-MM-DD"),
      summary: null,
      stops: [],
      customers: [],
      selectedCustomerId: query.get("customerId") || "",
      selectedDriver: query.get("driver") || "",
      selectedTruck: query.get("truckNo") || "",
      sortBy: "driver",
      expandedOrderId: "",
      linesByOrder: {},
      loadingLinesForOrder: "",
      loading: false,
      errorMessage: "",
      infoMessage: "",
      integrationNotConfigured: false,
    };
  }

  componentDidMount() {
    this.initialiseCustomerContext();
  }

  getCustomerId = () => {
    if (this.state.selectedCustomerId) {
      return this.state.selectedCustomerId;
    }

    try {
      const customer = JSON.parse(localStorage.getItem("customer"));
      return customer ? customer.customerId || customer.id || customer.CustomerId || "" : "";
    } catch (e) {
      return "";
    }
  };

  getCustomerName = () => {
    try {
      const customer = JSON.parse(localStorage.getItem("customer"));
      return customer ? customer.customerName || customer.name || customer.Name || "" : "";
    } catch (e) {
      return "";
    }
  };

  isSystemAdmin = () => {
    return (localStorage.getItem("role") || "").toString().toLowerCase() === "systemadmin";
  };

  initialiseCustomerContext = async () => {
    if (!this.isSystemAdmin()) {
      this.loadRunSheet();
      return;
    }

    try {
      const response = await axios.get(`${apiHost}/api/Customer/GetAllCustomers?AllRecords=true&SortDirection=asc&SortString=name`, {
        headers: this.getAuthHeaders(),
      });

      const customers = response.data || [];
      const storedCustomerId = this.getCustomerId();
      const storedCustomerName = this.getCustomerName();
      const matchedCustomer = customers.find((customer) => customer.id === storedCustomerId)
        || customers.find((customer) => (customer.name || "").toLowerCase() === storedCustomerName.toLowerCase())
        || null;

      this.setState(
        {
          customers,
          selectedCustomerId: matchedCustomer ? matchedCustomer.id : "",
        },
        this.loadRunSheet
      );
    } catch (e) {
      this.setState({
        errorMessage: "Unable to load customers for run sheet selection.",
      });
    }
  };

  getAuthHeaders = () => ({
    Authorization: "Bearer " + localStorage.getItem("token"),
  });

  buildQuery = (extra = {}) => {
    const params = new URLSearchParams();
    params.set("runDate", this.state.selectedDate);

    const customerId = this.getCustomerId();
    if (customerId) {
      params.set("customerId", customerId);
    }

    Object.keys(extra).forEach((key) => {
      if (extra[key]) {
        params.set(key, extra[key]);
      }
    });

    return params.toString();
  };

  loadRunSheet = async () => {
    if (this.isSystemAdmin() && !this.getCustomerId()) {
      this.setState({
        summary: null,
        stops: [],
        loading: false,
        errorMessage: "",
        infoMessage: "",
        integrationNotConfigured: false,
      });
      return;
    }

    this.setState({ loading: true, errorMessage: "", infoMessage: "", integrationNotConfigured: false });

    try {
      const customerId = this.getCustomerId();
      const integrationEnabled = await getRunSheetCapability(customerId, this.getAuthHeaders());
      if (!integrationEnabled) {
        this.setState({
          summary: null,
          stops: [],
          loading: false,
          integrationNotConfigured: true,
          errorMessage: "",
          infoMessage: "",
        });
        return;
      }

      const summaryResponse = await axios.get(`${apiHost}/api/RunSheets/summary?${this.buildQuery()}`, {
        headers: this.getAuthHeaders(),
      });

      const stopsResponse = await axios.get(`${apiHost}/api/RunSheets/stops?${this.buildQuery({
        driver: this.state.selectedDriver,
        truckNo: this.state.selectedTruck,
      })}`, {
        headers: this.getAuthHeaders(),
      });

      const stops = stopsResponse.data && stopsResponse.data.stops ? stopsResponse.data.stops : [];
      const dispatchDrivers = this.buildDispatchDrivers(stops);
      const apiSummary = summaryResponse.data || {};

      this.setState({
        summary: {
          ...apiSummary,
          runDate: stopsResponse.data && stopsResponse.data.runDate ? stopsResponse.data.runDate : apiSummary.runDate,
          stopCount: stops.length,
          driverCount: new Set(stops.map((stop) => (stop.driver || "").trim()).filter(Boolean)).size,
          truckCount: new Set(stops.map((stop) => (stop.truckNo || stop.vehicleRegistration || "").trim()).filter(Boolean)).size,
          drivers: dispatchDrivers,
        },
        stops,
        loading: false,
        infoMessage:
          stopsResponse.data && stopsResponse.data.isFallback
            ? `No run sheet data is available for ${moment(stopsResponse.data.requestedDate).format("dddd Do MMMM, YYYY")}. Showing the latest available run sheet from ${moment(stopsResponse.data.runDate).format("dddd Do MMMM, YYYY")}.`
            : "",
      });
    } catch (e) {
      const status = e && e.response ? e.response.status : null;
      const code = e && e.response && e.response.data ? e.response.data.code : "";
      if (status === 404 && code === "IntegrationNotConfigured") {
        cacheRunSheetCapability(this.getCustomerId(), false);
      }
      this.setState({
        loading: false,
        summary: null,
        stops: [],
        integrationNotConfigured: status === 404 && code === "IntegrationNotConfigured",
        errorMessage:
          status === 404 && code === "IntegrationNotConfigured"
            ? ""
            : status === 404
              ? "No run sheet data is available for the selected date."
              : "Unable to load run sheet data.",
        infoMessage: "",
      });
    }
  };

  handleDateChange = (event) => {
    this.setState(
      {
        selectedDate: event.target.value,
        selectedDriver: "",
        selectedTruck: "",
      },
      this.loadRunSheet
    );
  };

  handleCustomerChange = (event) => {
    this.setState(
      {
        selectedCustomerId: event.target.value,
        selectedDriver: "",
        selectedTruck: "",
        expandedOrderId: "",
        linesByOrder: {},
        infoMessage: "",
        integrationNotConfigured: false,
      },
      this.loadRunSheet
    );
  };

  handleDriverChange = (event) => {
    const value = event.target.value;
    const driver = this.driverOptions().find((item) => `${item.driver}|${item.truckNo}` === value);

    this.setState(
      {
        selectedDriver: driver ? driver.driver : "",
        selectedTruck: driver ? driver.truckNo : "",
      },
      this.loadRunSheet
    );
  };

  openDispatchSummary = () => {
    const params = new URLSearchParams();
    params.set("runDate", this.state.selectedDate);
    const customerId = this.getCustomerId();
    if (customerId) {
      params.set("customerId", customerId);
    }
    this.props.history.push(`/RunSheet-Dispatch-Summary?${params.toString()}`);
  };

  renderIntegrationMessage = () => {
    if (!this.state.integrationNotConfigured) {
      return null;
    }

    return (
      <section className="runsheetIntegrationMessage">
        <div className="runsheetIntegrationCopy">
          <img src={visionTrakLogo} alt="VisionTrak" />
          <span>ERP Integration</span>
          <h3>Connect your ERP run sheets to VisionTrak.</h3>
          <p>
            ERP run sheet integration is not currently enabled for your organisation. VisionTrak can import delivery runs, stops, driver assignments, truck details, and order information from supported ERP systems.
          </p>
          <p>
            To connect your current ERP solution, please contact our team and we will help confirm the available integration options.
          </p>
        </div>
        <div className="runsheetIntegrationGraphic" aria-hidden="true">
          <div className="runsheetSheetWave">
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
          </div>
          <div className="runsheetSystemNode runsheetSystemNodeErp">
            <strong>ERP</strong>
            <span>Orders</span>
          </div>
          <div className="runsheetDataFlow">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="runsheetSystemNode runsheetSystemNodeVision">
            <strong>VisionTrak</strong>
            <span>Run Sheets</span>
          </div>
          <div className="runsheetMiniSheet">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section>
    );
  };

  loadLines = async (orderId) => {
    if (!orderId) {
      return;
    }

    if (this.state.expandedOrderId === orderId) {
      this.setState({ expandedOrderId: "" });
      return;
    }

    if (this.state.linesByOrder[orderId]) {
      this.setState({ expandedOrderId: orderId });
      return;
    }

    this.setState({ expandedOrderId: orderId, loadingLinesForOrder: orderId });

    try {
      const response = await axios.get(`${apiHost}/api/RunSheets/lines/${encodeURIComponent(orderId)}?${this.buildQuery()}`, {
        headers: this.getAuthHeaders(),
      });

      this.setState((prevState) => ({
        linesByOrder: {
          ...prevState.linesByOrder,
          [orderId]: response.data || [],
        },
        loadingLinesForOrder: "",
      }));
    } catch (e) {
      this.setState((prevState) => ({
        linesByOrder: {
          ...prevState.linesByOrder,
          [orderId]: [],
        },
        loadingLinesForOrder: "",
      }));
    }
  };

  driverOptions = () => {
    const drivers = this.state.summary && this.state.summary.drivers ? this.state.summary.drivers : [];
    return drivers.map((driver) => ({
      driver: driver.driver || "",
      truckNo: driver.truckNo || "",
      label: `${driver.driver || "Unassigned"}${driver.truckNo ? ` / ${driver.truckNo}` : ""}`,
      stopCount: driver.stopCount || 0,
    }));
  };

  normaliseSortValue = (value) => {
    return (value || "").toString().trim().toLowerCase();
  };

  compareText = (left, right) => {
    return this.normaliseSortValue(left).localeCompare(this.normaliseSortValue(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  hasGpsCoordinates = (stop) => {
    const latitude = Number(stop.latitude);
    const longitude = Number(stop.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== 0 && longitude !== 0;
  };

  buildDispatchDrivers = (stops) => {
    const groups = {};

    stops.forEach((stop) => {
      const driver = (stop.driver || "").trim();
      const truckNo = (stop.truckNo || "").trim();
      const vehicleRegistration = (stop.vehicleRegistration || "").trim();
      const key = `${driver}|${truckNo}|${vehicleRegistration}`;
      const dropNumber = Number(stop.dropNumber);

      if (!groups[key]) {
        groups[key] = {
          driver,
          truckNo,
          vehicleRegistration,
          stopCount: 0,
          firstDrop: null,
          lastDrop: null,
          missingCoordinateCount: 0,
          creditStopCount: 0,
        };
      }

      const group = groups[key];
      group.stopCount += 1;
      group.missingCoordinateCount += this.hasGpsCoordinates(stop) ? 0 : 1;
      group.creditStopCount += stop.stopCredit ? 1 : 0;

      if (Number.isFinite(dropNumber)) {
        group.firstDrop = group.firstDrop === null ? dropNumber : Math.min(group.firstDrop, dropNumber);
        group.lastDrop = group.lastDrop === null ? dropNumber : Math.max(group.lastDrop, dropNumber);
      }
    });

    return Object.values(groups).map((group) => ({
      ...group,
      firstDrop: group.firstDrop === null ? "—" : group.firstDrop,
      lastDrop: group.lastDrop === null ? "—" : group.lastDrop,
    }));
  };

  getMissingGpsKey = (stop) => {
    return [
      stop.customerCode,
      stop.customerName,
      stop.address1,
      stop.address2,
      stop.address3,
      stop.address4,
      stop.postcode,
    ].map((value) => this.normaliseSortValue(value)).join("|");
  };

  dedupeMissingGpsStops = (stops) => {
    const uniqueStops = {};

    stops.forEach((stop) => {
      const key = this.getMissingGpsKey(stop);
      if (!uniqueStops[key]) {
        uniqueStops[key] = stop;
      }
    });

    return Object.values(uniqueStops);
  };

  missingGpsStops = () => {
    return this.dedupeMissingGpsStops(this.state.stops.filter((stop) => !this.hasGpsCoordinates(stop)))
      .slice()
      .sort((left, right) => this.compareText(left.driver, right.driver)
        || this.compareText(left.truckNo, right.truckNo)
        || Number(left.dropNumber || 0) - Number(right.dropNumber || 0)
        || this.compareText(left.customerName, right.customerName));
  };

  openMissingGpsPage = () => {
    const params = new URLSearchParams();
    params.set("runDate", this.state.selectedDate);

    const customerId = this.getCustomerId();
    if (customerId) {
      params.set("customerId", customerId);
    }

    this.props.history.push(`/RunSheet-Missing-GPS?${params.toString()}`);
  };

  groupedRuns = () => {
    const groups = {};

    this.state.stops.forEach((stop) => {
      const driver = stop.driver || "Unassigned";
      const truckNo = stop.truckNo || "";
      const registration = stop.vehicleRegistration || "";
      const truck = truckNo || registration;
      const key = `${driver}|${truck}`;

      if (!groups[key]) {
        groups[key] = {
          key,
          driver,
          truck,
          truckNo,
          registration,
          isVisiontrakLinked: stop.isVisiontrakLinked !== false,
          stops: [],
        };
      }

      groups[key].isVisiontrakLinked = groups[key].isVisiontrakLinked || stop.isVisiontrakLinked !== false;
      groups[key].stops.push(stop);
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        stops: group.stops.slice().sort((left, right) => {
          const leftDrop = Number(left.dropNumber || 0);
          const rightDrop = Number(right.dropNumber || 0);

          if (leftDrop !== rightDrop) {
            return leftDrop - rightDrop;
          }

          return this.compareText(left.customerName, right.customerName);
        }),
      }))
      .sort((left, right) => {
        if (this.state.sortBy === "truck") {
          return this.compareText(left.truck || "Unassigned", right.truck || "Unassigned")
            || this.compareText(left.driver, right.driver);
        }

        return this.compareText(left.driver, right.driver)
          || this.compareText(left.truck || "Unassigned", right.truck || "Unassigned");
      });
  };

  getRunDropRange = (stops) => {
    const dropNumbers = stops
      .map((stop) => Number(stop.dropNumber || 0))
      .filter((dropNumber) => dropNumber > 0);

    if (!dropNumbers.length) {
      return "Drop order";
    }

    return `Drops ${Math.min(...dropNumbers)}-${Math.max(...dropNumbers)}`;
  };

  getTruckLabel = (run) => {
    if (run.truckNo && run.registration) {
      return `Truck ${run.truckNo} (${run.registration})`;
    }

    if (run.truckNo) {
      return `Truck ${run.truckNo}`;
    }

    if (run.registration) {
      return `Rego ${run.registration}`;
    }

    return "No truck assigned";
  };

  escapeCsvValue = (value) => {
    if (value === null || typeof value === "undefined") {
      return "";
    }

    let text = value.toString();
    if (typeof value === "string" && /^[=+\-@]/.test(text)) {
      text = `'${text}`;
    }

    return `"${text.replace(/"/g, '""')}"`;
  };

  exportRunToCsv = (run) => {
    const columns = [
      ["Run Date", () => this.state.selectedDate],
      ["Driver", (stop) => stop.driver || "Unassigned"],
      ["Truck Number", (stop) => stop.truckNo || ""],
      ["Vehicle Registration", (stop) => stop.vehicleRegistration || ""],
      ["Drop", (stop) => stop.dropNumber],
      ["Order ID", (stop) => stop.orderId || ""],
      ["Customer Code", (stop) => stop.customerCode || ""],
      ["Customer Name", (stop) => stop.customerName || ""],
      ["Address", (stop) => [stop.address1, stop.address2, stop.address3, stop.address4, stop.postcode].filter(Boolean).join(", ")],
      ["Delivery Instructions", (stop) => stop.deliveryInstructions && stop.deliveryInstructions !== "." ? stop.deliveryInstructions : ""],
      ["Line Count", (stop) => stop.lineCount || 0],
      ["Credit Stop", (stop) => stop.stopCredit ? "Yes" : "No"],
      ["Latitude", (stop) => stop.latitude === null || typeof stop.latitude === "undefined" ? "" : stop.latitude],
      ["Longitude", (stop) => stop.longitude === null || typeof stop.longitude === "undefined" ? "" : stop.longitude],
      ["VisionTrak Linked", (stop) => stop.isVisiontrakLinked === false ? "No" : "Yes"],
    ];
    const rows = [
      columns.map(([heading]) => this.escapeCsvValue(heading)).join(","),
      ...run.stops.map((stop) => columns.map(([, getValue]) => this.escapeCsvValue(getValue(stop))).join(",")),
    ];
    const filePart = (value, fallback) => (value || fallback)
      .toString()
      .trim()
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/^-+|-+$/g, "") || fallback;
    const fileName = `run-sheet-${this.state.selectedDate}-${filePart(run.driver, "unassigned")}-${filePart(run.truck, "no-truck")}.csv`;
    const blob = new Blob(["\ufeff", rows.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  renderLineItems = (stop, colSpan) => {
    const lines = this.state.linesByOrder[stop.orderId] || [];
    const isExpanded = this.state.expandedOrderId === stop.orderId;
    const isLoadingLines = this.state.loadingLinesForOrder === stop.orderId;

    if (!isExpanded) {
      return null;
    }

    return (
      <tr className="runsheetLinesRow">
        <td colSpan={colSpan}>
          {isLoadingLines ? (
            <div className="runsheetLineState">Loading line items...</div>
          ) : lines.length ? (
            <table className="runsheetLinesTable">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.lineNo}</td>
                    <td>{line.itemCode}</td>
                    <td>{line.itemName}</td>
                    <td>{line.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="runsheetLineState">No line items returned for this order.</div>
          )}
        </td>
      </tr>
    );
  };

  renderSummary = () => {
    const { summary } = this.state;
    if (!summary) {
      return null;
    }
    const missingGpsCount = this.missingGpsStops().length;

    return (
      <div className="runsheetMetrics">
        <div className="runsheetMetric">
          <span>Stops</span>
          <strong>{summary.stopCount || 0}</strong>
        </div>
        <div className="runsheetMetric">
          <span>Drivers</span>
          <strong>{summary.driverCount || 0}</strong>
        </div>
        <div className="runsheetMetric">
          <span>Trucks</span>
          <strong>{summary.truckCount || 0}</strong>
        </div>
        <div className="runsheetMetric runsheetMetricWarning">
          <span>Missing GPS</span>
          <div className="runsheetMetricValueRow">
            <strong>{missingGpsCount}</strong>
            {missingGpsCount > 0 && (
              <button type="button" className="runsheetMetricAction" onClick={this.openMissingGpsPage}>
                View
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderStops = () => {
    if (this.state.integrationNotConfigured || this.state.errorMessage) {
      return null;
    }

    if (!this.state.stops.length) {
      return <div className="runsheetEmpty">No stops to show.</div>;
    }

    const runs = this.groupedRuns();

    return (
      <div className="runsheetRunList">
        {runs.map((run) => (
          <section className={run.isVisiontrakLinked ? "runsheetRunTile" : "runsheetRunTile unlinked"} key={run.key}>
            <div className="runsheetRunHeader">
              <div>
                <h3>{this.state.sortBy === "truck" ? this.getTruckLabel(run) : run.driver}</h3>
                <span>
                  {this.state.sortBy === "truck"
                    ? `Driver ${run.driver}`
                    : this.getTruckLabel(run)}
                </span>
                {!run.isVisiontrakLinked && <em className="runsheetUnlinkedBadge">Not linked to a Visiontrak asset</em>}
              </div>
              <div className="runsheetRunMeta">
                <span>{run.stops.length} stops</span>
                <span>{this.getRunDropRange(run.stops)}</span>
                <button type="button" className="runsheetExportButton" onClick={() => this.exportRunToCsv(run)}>
                  Export CSV
                </button>
              </div>
            </div>
            <div className="runsheetTableWrap">
              <table className="runsheetTable">
                <thead>
                  <tr>
                    <th>Drop</th>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Instructions</th>
                    <th>Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {run.stops.map((stop) => {
                    const isExpanded = this.state.expandedOrderId === stop.orderId;
                    const hasGpsCoordinates = this.hasGpsCoordinates(stop);

                    return (
                      <React.Fragment key={stop.id}>
                        <tr className={hasGpsCoordinates ? "" : "runsheetMissingGpsRow"}>
                          <td className="runsheetDropCell">
                            <span className="runsheetDropBadge">{stop.dropNumber}</span>
                          </td>
                          <td>
                            <strong className="runsheetCustomerName">{stop.customerName}</strong>
                            <span>{stop.customerCode}</span>
                            {!hasGpsCoordinates && <em className="runsheetGpsBadge">Missing GPS</em>}
                          </td>
                          <td className="runsheetAddressCell">
                            {[stop.address1, stop.address2, stop.address3, stop.address4, stop.postcode]
                              .filter(Boolean)
                              .join(", ")}
                          </td>
                          <td className={stop.deliveryInstructions && stop.deliveryInstructions !== "." ? "" : "runsheetMutedCell"}>
                            {stop.deliveryInstructions && stop.deliveryInstructions !== "." ? stop.deliveryInstructions : "No instructions"}
                          </td>
                          <td>
                            <button type="button" className="runsheetLineButton" onClick={() => this.loadLines(stop.orderId)}>
                              {isExpanded ? "Hide" : "View"}
                            </button>
                          </td>
                        </tr>
                        {this.renderLineItems(stop, 5)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    );
  };

  render() {
    const driverValue =
      this.state.selectedDriver || this.state.selectedTruck
        ? `${this.state.selectedDriver}|${this.state.selectedTruck}`
        : "";
    const needsCustomerSelection = this.isSystemAdmin() && !this.getCustomerId();

    return (
      <Layout>
        <Helmet>
          <title>Run Sheets</title>
        </Helmet>
        <div className="runsheetPage">
          <section className="runsheetHero">
            <div className="runsheetToolbar">
              <div>
                <h2>Run Sheets</h2>
                <p>{this.state.summary ? moment(this.state.summary.runDate).format("dddd Do MMMM, YYYY") : "Daily delivery runs"}</p>
              </div>
              <div className="runsheetFilters">
                {this.isSystemAdmin() && (
                  <select value={this.state.selectedCustomerId} onChange={this.handleCustomerChange}>
                    <option value="">Select customer</option>
                    {this.state.customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                )}
                <input type="date" value={this.state.selectedDate} onChange={this.handleDateChange} />
                <select value={driverValue} onChange={this.handleDriverChange}>
                  <option value="">All runs</option>
                  {this.driverOptions().map((driver) => (
                    <option key={`${driver.driver}|${driver.truckNo}`} value={`${driver.driver}|${driver.truckNo}`}>
                      {driver.label}
                    </option>
                  ))}
                </select>
                <select value={this.state.sortBy} onChange={(event) => this.setState({ sortBy: event.target.value })}>
                  <option value="driver">Sort by driver</option>
                  <option value="truck">Sort by truck</option>
                </select>
                <button type="button" onClick={this.loadRunSheet} disabled={this.state.loading}>
                  Refresh
                </button>
                <button type="button" className="runsheetDispatchButton" onClick={this.openDispatchSummary} disabled={!this.state.summary || this.state.loading}>
                  Run Sheet Summary
                </button>
              </div>
            </div>
            {this.renderSummary()}
          </section>

          {needsCustomerSelection && <div className="runsheetError">Select a customer to load run sheet data.</div>}
          {this.state.errorMessage && <div className="runsheetError">{this.state.errorMessage}</div>}
          {this.state.infoMessage && <div className="runsheetInfo">{this.state.infoMessage}</div>}
          {this.renderIntegrationMessage()}
          {this.state.loading ? <div className="runsheetLoading">Loading run sheet...</div> : this.renderStops()}
        </div>
      </Layout>
    );
  }
}

export default RunSheets;
